// AADHYAA — Centralized route title + meta description manager
// Sets document.title AND meta description automatically based on current pathname.
// Mounts once in App.js inside <Router>; pages with their own <Helmet> override this.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Default fallback description (matches index.html)
const DEFAULT_DESCRIPTION = 'Personal Vedic guidance for timing, decisions, and life clarity. Know when to act. Know when to wait.';
const DEFAULT_OG_IMAGE = '/og/default.png';
const DEFAULT_OG_IMAGE_ALT = 'AADHYAA — Family-First Vedic Astrology';

// Per-route meta (title + description) — covers all 90 routes
const ROUTE_META = {
  // Public entry
  '/': {
    title: 'AADHYAA — Vedic Astrology, Done Right',
    description: 'Personal Vedic guidance for timing, decisions, and life clarity. AI-enhanced Jyotish with multi-system validation. Not predictions. Practice.',
  },
  '/auth': {
    title: 'Sign In — AADHYAA',
    description: 'Sign in or create your free AADHYAA account to access your personalized Vedic birth chart, daily horoscope, and 12 premium reports.',
  },
  '/reset-password': {
    title: 'Reset Password — AADHYAA',
    description: 'Reset your AADHYAA account password securely.',
  },
  '/pricing': {
    title: 'Pricing — Free, MARGA, KULA Plans | AADHYAA',
    description: 'Choose your AADHYAA plan: PRARAMBH (Free), MARGA (₹899/mo, 7-day free trial), or KULA (₹1,999/mo for families). All 12 premium reports + multi-system Vedic analysis.',
    image: '/og/pricing.png',
    imageAlt: 'AADHYAA Pricing — PRARAMBH, MARGA, KULA plans',
  },
  '/about': {
    title: 'About AADHYAA — Our Mission',
    description: 'AADHYAA combines classical Vedic Jyotish wisdom with modern AI to deliver personal guidance for timing, decisions, and life clarity.',
  },
  '/methodology': {
    title: 'Our Vedic Methodology — AADHYAA',
    description: 'How AADHYAA blends 8 classical Vedic sources (BPHS, Saravali, Phaladeepika) with multi-system dasha validation for 99% chart accuracy.',
  },
  '/glossary': {
    title: 'Vedic Astrology Glossary — AADHYAA',
    description: 'Definitions of Vedic astrology terms: nakshatra, dasha, rashi, lagna, yoga, kuta, varga, panchang, and more. Free reference.',
  },

  // Public SEO
  '/insights': {
    title: 'Insights — Vedic Wisdom Articles | AADHYAA',
    description: 'Deep articles on Vedic astrology, decision timing, relationship compatibility, and life phases. Authored by AADHYAA editorial team.',
  },
  '/learn': {
    title: 'Learn Vedic Astrology — AADHYAA',
    description: 'Beginner to advanced Vedic astrology resources: nakshatras, rashis, dashas, yogas. Free guides + interactive tools.',
    image: '/og/learn.png',
    imageAlt: 'Learn Vedic Astrology — Rashis · Nakshatras · Kundli',
  },
  '/learn/rashi': {
    title: '12 Rashis (Vedic Zodiac Signs) — Complete Guide | AADHYAA',
    description: 'Explore all 12 rashis (Mesha, Vrishabha, Mithuna, Karka, Simha, Kanya, Tula, Vrishchika, Dhanu, Makara, Kumbha, Meena) with Vedic interpretations.',
    image: '/og/learn-rashi.png',
    imageAlt: 'All 12 Vedic Rashis — Mesha to Meena',
  },
  '/learn/nakshatra': {
    title: '27 Nakshatras (Vedic Lunar Mansions) — Complete Guide | AADHYAA',
    description: 'Discover all 27 nakshatras from Ashwini to Revati. Personality, career, spiritual path, and dasha periods for each Vedic lunar mansion.',
    image: '/og/learn-nakshatra.png',
    imageAlt: 'All 27 Vedic Nakshatras — Ashwini to Revati',
  },
  '/learn/kundli': {
    title: 'Free Vedic Kundli Generator — Birth Chart, Nakshatra, Rashi | AADHYAA',
    description: 'Generate your free Vedic kundli (birth chart) instantly. Discover your janma nakshatra, rashi, lagna, and planetary positions. No signup required.',
    image: '/og/learn-kundli.png',
    imageAlt: 'AADHYAA Free Vedic Kundli Generator',
  },
  '/learn/compatibility': {
    title: 'Free Kundli Matching (Ashtakoot) — Vedic Compatibility Score | AADHYAA',
    description: 'Check Vedic marriage compatibility (kundli milan) between two birth charts. Free Ashtakoot 36-point score with breakdown across 8 koots.',
    image: '/og/learn-compatibility.png',
    imageAlt: 'AADHYAA Free Vedic Kundli Matching — Ashtakoot 36-point',
  },

  // Legal/Policy
  '/legal': { title: 'Legal — AADHYAA', description: 'Legal information, terms, and policies for AADHYAA users.' },
  '/terms': { title: 'Terms of Service — AADHYAA', description: 'Terms of service for AADHYAA subscriptions and usage.' },
  '/privacy': { title: 'Privacy Policy — AADHYAA', description: 'How AADHYAA collects, uses, and protects your personal data.' },
  '/disclaimers': { title: 'Disclaimers — AADHYAA', description: 'Important disclaimers about Vedic astrology guidance and limitations.' },
  '/ai-policy': { title: 'AI Policy — AADHYAA', description: 'Our policy on AI-augmented Vedic interpretation and data usage.' },
  '/data-policy': { title: 'Data Policy — AADHYAA', description: 'How AADHYAA stores, processes, and protects user data.' },
  '/health-policy': { title: 'Health Policy — AADHYAA', description: 'AADHYAA guidance on wellness, health, and medical disclaimers.' },
  '/consent-policy': { title: 'Consent Policy — AADHYAA', description: 'User consent policy for data collection, AI processing, and family member management.' },
  '/ethics': { title: 'Ethics & Principles — AADHYAA', description: 'AADHYAA ethical principles for Vedic guidance, AI integrity, and family privacy.' },

  // Main app
  '/dashboard': { title: 'My Snapshot — AADHYAA', description: 'Your daily Vedic snapshot: life phase, focus areas, energy rhythm, and personalized guidance.' },
  '/home': { title: 'Home — AADHYAA', description: 'Your AADHYAA home dashboard.' },
  '/my-life': { title: 'My Life — AADHYAA', description: 'Deep insights into your career, health, relationships, wealth, and spiritual path based on your Vedic chart.' },
  '/my-stars': { title: 'My Stars — AADHYAA', description: 'Your planetary positions, dashas, and astrological transits in real time.' },
  '/kundli': { title: 'My Kundli — Vedic Birth Chart | AADHYAA', description: 'Your complete Vedic kundli with planet positions, houses, dashas, yogas, divisional charts, and planetary strengths.' },
  '/astro-report': { title: 'Astrological Reading — AADHYAA', description: 'Your full astrological reading with multi-system Vedic analysis.' },
  '/reports': { title: 'Premium Vedic Reports — AADHYAA', description: '12 premium Vedic reports including Life Phase Evolution, Career Timing, Marriage Compatibility, and Advanced Multi-System analysis.' },
  '/reports-catalog': { title: 'Reports Catalog — AADHYAA', description: 'Browse 12 premium Vedic reports tailored to your life decisions and timing needs.' },
  '/my-purchases': { title: 'My Purchases — AADHYAA', description: 'Your purchased reports and consultation history.' },
  '/purchase-history': { title: 'Purchase History — AADHYAA', description: 'Complete history of your AADHYAA purchases and subscriptions.' },
  '/report-repository': { title: 'Report Repository — AADHYAA', description: 'Your saved Vedic reports and downloadable PDFs.' },
  '/horoscope': { title: 'Daily Horoscope — AADHYAA', description: 'Your personalized daily, weekly, and monthly Vedic horoscope with confidence scores and reasoning.' },
  '/today': { title: "Today's Cosmic View — AADHYAA", description: 'Today\'s planetary positions, panchang, and personalized guidance for your day.' },

  // Timing
  '/ekadashi': { title: 'Ekadashi Calendar 2026 — Fasting Dates & Guidelines | AADHYAA', description: 'Complete Ekadashi calendar with upcoming dates, fasting guidelines, and spiritual significance.' },
  '/panchang': { title: 'Today\'s Panchang — Vedic Almanac | AADHYAA', description: 'Today\'s personalized panchang with tithi, nakshatra, yoga, karana, and auspicious timings for your location.' },
  '/muhurta': { title: 'Muhurta — Auspicious Timing | AADHYAA', description: 'Find the most auspicious timing (muhurta) for marriage, business launch, travel, and other life events.' },
  '/muhurta/compare': { title: 'Compare Muhurtas — AADHYAA', description: 'Compare auspicious timings across multiple dates to find your best Vedic muhurta.' },
  '/timing': { title: 'Timing Decisions — AADHYAA', description: 'Multi-system timing analysis for your life decisions: when to act, when to wait, when to invest.' },
  '/ritual': { title: 'Guided Rituals — AADHYAA', description: 'Personalized Vedic rituals for prosperity, health, relationships, and spiritual growth.' },
  '/guided-rituals': { title: 'Guided Rituals — AADHYAA', description: 'Step-by-step Vedic rituals tailored to your birth chart and current planetary periods.' },
  '/mantras': { title: 'Mantras Library — AADHYAA', description: 'Personalized mantras for each planet and life area, with pronunciation and chanting instructions.' },
  '/remedies': { title: 'Vedic Remedies — AADHYAA', description: 'Authentic Vedic remedies (upayas) for planetary doshas, based on your birth chart and current dashas.' },

  // Family / Relationships
  '/compatibility': { title: 'Compatibility Analysis — AADHYAA', description: 'Deep Vedic compatibility analysis using Ashtakoot, Manglik, Nadi, and Dasha synchronicity.' },
  '/marriage-compatibility': { title: 'Marriage Compatibility — AADHYAA', description: 'Complete Vedic marriage compatibility report with Ashtakoot, Manglik dosha, and remedies.' },
  '/family': { title: 'Family — AADHYAA', description: 'Manage your family members\' Vedic profiles and access family-wide insights.' },
  '/family-dashboard': { title: 'Family Dashboard — AADHYAA', description: 'Unified Vedic dashboard for your entire family\'s timing, compatibility, and wellbeing.' },
  '/family-intelligence': { title: 'Family Intelligence — AADHYAA', description: 'AI-powered family intelligence: shared transits, relationship dynamics, and collective guidance.' },
  '/relationships': { title: 'Relationships — AADHYAA', description: 'Vedic relationship insights for partners, family, and children.' },

  // Health
  '/health-connect': { title: 'Health Connect — AADHYAA', description: 'Connect Vedic timing with your health practices for optimal wellbeing.' },
  '/circadian': { title: 'Circadian Rhythm — AADHYAA', description: 'Align your daily routine with Vedic dinacharya and your personal circadian rhythm.' },
  '/habits': { title: 'Habits Tracker — AADHYAA', description: 'Track your daily habits aligned with Vedic principles and planetary energies.' },
  '/mood': { title: 'Mood Tracker — AADHYAA', description: 'Track your mood and emotions correlated with planetary transits and lunar phases.' },

  // AI / Intelligence / Learn
  '/ask': { title: 'Ask AADHYAA — AI Vedic Assistant', description: 'Ask any Vedic astrology question and get personalized AI-powered answers grounded in your birth chart.' },
  '/ai-intelligence': { title: 'AI Intelligence — AADHYAA', description: 'AI-powered Vedic insights combining classical Jyotish with modern machine learning.' },
  '/astro-intelligence': { title: 'Astro Intelligence — AADHYAA', description: 'Multi-system astrological intelligence for confident life decisions.' },
  '/dasha-explorer': { title: 'Dasha Explorer — Vimshottari, Yogini & More | AADHYAA', description: 'Explore your current and future dasha periods across 5 Vedic systems: Vimshottari, Yogini, Kalachakra, Chara, Ashtottari.' },
  '/lifepath': { title: 'Life Path — AADHYAA', description: 'Your Vedic life path: karmic patterns, dharma, and major life transitions.' },
  '/prashna-varshphal': { title: 'Prashna & Varshphal — AADHYAA', description: 'Vedic horary (Prashna) and annual chart (Varshphal) for specific questions and yearly forecasts.' },
  '/learn-grow': { title: 'Learn & Grow — Vedic Wisdom | AADHYAA', description: 'Learn Vedic astrology, meditation practices, and spiritual growth techniques.' },
  '/knowledge': { title: 'Knowledge Hub — AADHYAA', description: 'Deep Vedic knowledge: nakshatras, yogas, dashas, divisional charts, and classical interpretations.' },
  '/vedic-101': { title: 'Vedic 101 — Beginner\'s Guide | AADHYAA', description: 'Start your Vedic astrology journey with our beginner-friendly guide. Free, structured, step-by-step.' },
  '/vedic-depth': { title: 'Vedic Depth — Advanced Study | AADHYAA', description: 'Advanced Vedic astrology: divisional charts, yogas, classical references, and master-level techniques.' },
  '/kids-courses': { title: 'Kids Gurukul — 304 Hindu Stories for Kids | AADHYAA', description: '304 timeless Hindu stories with audio narration and Hindi translation. Build values and Vedic wisdom in your kids.' },
  '/life-readings': { title: 'My Life Readings — AADHYAA', description: 'Your collection of detailed Vedic life readings and chart interpretations.' },

  // Account
  '/settings': { title: 'Settings — AADHYAA', description: 'Manage your AADHYAA preferences, notifications, language, and privacy settings.' },
  '/account': { title: 'Account — AADHYAA', description: 'Manage your AADHYAA account, subscription, and billing details.' },
  '/notifications': { title: 'Notifications — AADHYAA', description: 'Your Vedic timing alerts, transit notifications, and important reminders.' },
  '/share': { title: 'Share AADHYAA', description: 'Invite friends and family to AADHYAA and earn rewards.' },
  '/tools': { title: 'Tools — AADHYAA', description: 'AADHYAA tools for kundli generation, compatibility, panchang, muhurta, and more.' },
  '/checklist': { title: 'Daily Checklist — AADHYAA', description: 'Your daily Vedic-aligned checklist of priorities and actions.' },
  '/onboarding': { title: 'Get Started — AADHYAA', description: 'Set up your AADHYAA profile with birth details and intent.' },
  '/getting-started': { title: 'Getting Started — AADHYAA', description: 'Quick start guide for new AADHYAA users.' },
  '/verified-reading': { title: 'Verified Reading by Expert — AADHYAA', description: 'Get your Vedic chart verified by a human expert astrologer.' },
  '/consultation': { title: 'Book a Consultation — AADHYAA', description: 'Book a 1-on-1 Vedic astrology consultation with a verified expert.' },

  // Admin
  '/admin': { title: 'Admin — AADHYAA', description: 'AADHYAA admin console.' },
  '/admin/analytics': { title: 'Content Performance — Admin | AADHYAA', description: 'Internal analytics dashboard.' },
  '/beta-admin': { title: 'Beta Admin — AADHYAA', description: 'Beta admin console.' },
  '/enterprise-admin': { title: 'Enterprise Admin — AADHYAA', description: 'Enterprise admin console.' },
  '/analytics': { title: 'Analytics Dashboard — AADHYAA', description: 'Internal analytics dashboard.' },
  '/conversion-dashboard': { title: 'Conversion Dashboard — AADHYAA', description: 'Conversion tracking dashboard.' },
  '/accuracy': { title: 'Accuracy Dashboard — AADHYAA', description: 'Track AADHYAA\'s prediction accuracy across users.' },
  '/trust-dashboard': { title: 'Trust Dashboard — AADHYAA', description: 'Trust and transparency metrics for AADHYAA users.' },
  '/privacy-dashboard': { title: 'Privacy Dashboard — AADHYAA', description: 'Your privacy controls and data management.' },

  // Subscription
  '/subscription/success': { title: 'Subscription Active — AADHYAA', description: 'Your AADHYAA subscription is active. Start exploring your premium features.' },
  '/subscription/cancel': { title: 'Subscription Cancelled — AADHYAA', description: 'Your AADHYAA subscription has been cancelled.' },
};

/**
 * Set or update a <meta name="..." content="..."> in <head>.
 */
const setMetaTag = (name, content) => {
  if (typeof document === 'undefined') return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

/**
 * Set or update an OG <meta property="..." content="..."> in <head>.
 */
const setOgTag = (property, content) => {
  if (typeof document === 'undefined') return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

/**
 * Set or update a <link rel="..." href="..."> in <head>.
 */
const setLinkTag = (rel, href) => {
  if (typeof document === 'undefined') return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const cap = (s) => (s || '').replace(/(^|\s)\S/g, (t) => t.toUpperCase());

/**
 * Auto-sets document.title + meta description + OG/Twitter on every route change.
 * Falls back to a sensible default for dynamic routes.
 */
const RouteTitle = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    let meta = ROUTE_META[path];

    // Dynamic / wildcard route handling
    if (!meta) {
      if (path.startsWith('/learn/rashi/')) {
        const slug = decodeURIComponent(path.split('/').pop());
        const name = cap(slug);
        meta = {
          title: `${name} Rashi — Vedic Astrology Profile | AADHYAA`,
          description: `${name} rashi (Vedic zodiac sign): personality, strengths, challenges, career path, relationships, and spiritual journey. Ruled by classical Vedic principles.`,
          image: `/og/rashi/${encodeURIComponent(slug)}.png`,
          imageAlt: `${name} Rashi — Vedic Zodiac Sign`,
        };
      } else if (path.startsWith('/learn/nakshatra/')) {
        const slug = decodeURIComponent(path.split('/').pop());
        const name = cap(slug);
        meta = {
          title: `${name} Nakshatra — Personality, Career & Spiritual Path | AADHYAA`,
          description: `${name} nakshatra: deity, personality, career, compatibility, and dasha periods. Free Vedic interpretation by AADHYAA.`,
          image: `/og/nakshatra/${encodeURIComponent(slug.toLowerCase())}.png`,
          imageAlt: `${name} Nakshatra — Vedic Lunar Mansion`,
        };
      } else if (path.startsWith('/insights/')) {
        meta = {
          title: 'Insights — AADHYAA',
          description: 'Deep Vedic wisdom articles on timing, decisions, and life clarity.',
        };
      } else if (path.startsWith('/report/')) {
        const slug = path.split('/').pop();
        const name = cap(slug.replace(/-/g, ' '));
        meta = {
          title: `${name} Report — AADHYAA`,
          description: `Your personalized ${name.toLowerCase()} Vedic report with multi-system analysis.`,
        };
      } else {
        meta = { title: 'AADHYAA — Vedic Astrology', description: DEFAULT_DESCRIPTION };
      }
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const canonical = `${origin}${path}`;
    const imagePath = meta.image || DEFAULT_OG_IMAGE;
    // Absolute URL for og:image (social platforms require absolute)
    const imageUrl = imagePath.startsWith('http') ? imagePath : `${origin}${imagePath}`;
    const imageAlt = meta.imageAlt || DEFAULT_OG_IMAGE_ALT;

    document.title = meta.title;
    setMetaTag('description', meta.description);
    // OG
    setOgTag('og:title', meta.title);
    setOgTag('og:description', meta.description);
    setOgTag('og:url', canonical);
    setOgTag('og:image', imageUrl);
    setOgTag('og:image:width', '1200');
    setOgTag('og:image:height', '630');
    setOgTag('og:image:alt', imageAlt);
    // Twitter
    setMetaTag('twitter:title', meta.title);
    setMetaTag('twitter:description', meta.description);
    setMetaTag('twitter:image', imageUrl);
    setMetaTag('twitter:card', 'summary_large_image');
    // Canonical
    setLinkTag('canonical', canonical);
  }, [location.pathname]);

  return null;
};

export default RouteTitle;
